import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Compact duration text ("30m", "1h30m", "2d") -> milliseconds. Shared by the
// duration keyword and the reminder-offset token so the two grammars cannot drift.
export const parseCompactDurationToMs = (text: string) => {
  const days = Number(text.match(/(\d+)\s*d/i)?.[1] ?? 0);
  const hours = Number(text.match(/(\d+)\s*h/i)?.[1] ?? 0);
  const minutes = Number(text.match(/(\d+)\s*m/i)?.[1] ?? 0);
  return days * DAY_MS + hours * HOUR_MS + minutes * MINUTE_MS;
};

// Milliseconds since midnight -> the clock token the NLP input renders
// ("09:00" / "9:00am"), matching the user's configured time format.
export const formatClockToken = (
  msOfDay: number,
  timeFormat: TimeFormatOptions,
) => parseDateTime(msOfDay, timeFormat).toLowerCase().replace(' ', '');

/**
 * Parses a time string (e.g., "9am", "14:30") into hours and minutes.
 */
export const parseTime = (timeStr: string | null) => {
  if (!timeStr) {
    return null;
  }
  const originalTimeStr = timeStr;
  timeStr = timeStr.toLowerCase();
  const isPm = timeStr.includes('pm');
  const isAm = timeStr.includes('am');
  const time = timeStr.replace(/am|pm/i, '');
  const parts = time.split(':');

  let hours = parseInt(parts[0], 10);
  const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0;

  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }
  if (isPm && hours < 12) {
    hours += 12;
  }
  // Midnight case
  if (isAm && hours === 12) {
    hours = 0;
  }

  if (hours < 0 || hours > 24) {
    return null;
  }
  if (minutes < 0 || minutes > 59) {
    return null;
  }
  if (hours === 24 && minutes > 0) {
    return null;
  }
  // 24:00 is the start of the next day
  if (hours === 24) {
    hours = 0;
  }

  return {
    hours,
    minutes,
    original: originalTimeStr,
    inMilliseconds: hours * 60 * 60 * 1000 + minutes * 60 * 1000,
  };
};
